import {Chip, Grid, IconButton, Stack, Table, TableRow, Tooltip, Typography} from "@mui/material";
import {AdjustRounded, DoneRounded} from "@mui/icons-material";
import Box from "@mui/material/Box";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {formatNumber} from "../../../reducer/actions/formatNumber";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {MoreOptions} from "../../Table/assets/MoreOptions";
import React from "react";
import {styled} from "@mui/material/styles";
import Paper from "@mui/material/Paper";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
}));
const getServiceRowType = (row: any) => {
  if (!row?.service_type_id) return 'unit';
  return row.service_type_id === 1 || row.service_type_id === 2 ? 'unit' : 'var';
};

export function ServicePackageCard({
                              item,
                              selectable,
                              selectedServiceGroupId,
                              onSelect,
                              onEdit,
                              onDelete,
                              isAdmin,
                              isFinance,
                              setOpenViewPrices,
                            }: any) {
  const isSelected = selectable && selectedServiceGroupId === item.id;

  return (
    <Grid item xs={12} md={12} key={item.id}>
      <Item
        onClick={() => {
          if (!selectable) return;
          onSelect(item.id);
        }}
        sx={{
          cursor: selectable ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          border: isSelected ? '2px solid #256861' : '2px solid transparent',
          boxShadow: isSelected ? '0 0 0 4px rgba(37, 104, 97, 0.15)' : 'none',
          opacity: selectable ? 1 : 0.92,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: 14 }} color="text.secondary">
              {item?.name}
            </Typography>

            {!selectable && (
              <Chip size="small" label="Included" sx={{ fontWeight: 600 }} />
            )}
          </Stack>

          {isSelected && (
            <Chip size="small" label="Selected" color="success" sx={{ fontWeight: 600 }} />
          )}

          <Stack direction="row" gap={1}>
            <Tooltip title="Default">
              <Typography variant="caption">
                Default{' '}
                <AdjustRounded
                  fontSize="small"
                  color={item?.default ? 'primary' : 'disabled'}
                />
              </Typography>
            </Tooltip>

            <Tooltip title="Extra">
              <Typography variant="caption">
                Extra{' '}
                <AdjustRounded
                  fontSize="small"
                  color={item?.extra ? 'primary' : 'disabled'}
                />
              </Typography>
            </Tooltip>

            <Tooltip title="Assigned Staff">
              <Typography variant="caption">
                Assigned Staff{' '}
                <AdjustRounded
                  fontSize="small"
                  color={item?.assigned_staff ? 'primary' : 'disabled'}
                />
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ m: '10px', background: '#f5f5f5', borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small" width="100%">
            <TableHead
              sx={{
                '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                  border: 'none',
                  fontSize: '14px',
                  p: '10px',
                  color: '#fff',
                },
                background: 'rgb(37 104 97 / 82%)',
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    minWidth: '150px',
                    width: '150px',
                  }}
                >Service Provider</TableCell>
                <TableCell
                sx={{
                  minWidth: '200px',
                  width: '200px',
                }}
                >Services</TableCell>
                <TableCell
                  sx={{
                    minWidth: '150px',
                    width: '150px',
                  }}
                >Staff Required</TableCell>
                <TableCell
                  sx={{
                    minWidth: '200px',
                    width: '200px',
                  }}
                >Includes</TableCell>
                <TableCell
                  sx={{
                    minWidth: '100px',
                    width: '100px',
                  }}
                >Cost Net</TableCell>
                <TableCell
                  sx={{
                    minWidth: '50px',
                    width: '50px',
                  }}
                >VAT</TableCell>
                <TableCell
                  sx={{
                    minWidth: '100px',
                    width: '100px',
                  }}
                >Cost Gross</TableCell>
                <TableCell
                  sx={{
                    minWidth: '100px',
                    width: '100px',
                  }}
                >Currency</TableCell>
                <TableCell
                  sx={{
                    minWidth: '100px',
                    width: '100px',
                  }}
                >Type</TableCell>
                <TableCell
                sx={{
                  minWidth: '50px',
                  width: '50px',
                }}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {(item?.services ?? []).map((row: any) => {
                const type = getServiceRowType(row);

                return (
                  <TableRow
                    key={row.id}
                    sx={{ '& .MuiTableCell-root': { padding: '5px' } }}
                  >
                    <TableCell>{row?.service_provider?.name}</TableCell>

                    <TableCell
                      sx={{
                        minWidth: 300,
                        width: 300,
                        cursor: 'move',
                        '&:hover': { background: '#f3f3f3' },
                      }}
                    >
                      {row?.name}
                    </TableCell>

                    <TableCell>
                      {row?.staff_required ? (
                        <DoneRounded color="success" />
                      ) : (
                        <CloseRoundedIcon color="disabled" />
                      )}
                    </TableCell>

                    <TableCell
                      dangerouslySetInnerHTML={{ __html: row?.includes_notes }}
                    />

                    <TableCell align="right">
                      {type === 'unit' ? (
                        <>{formatNumber(row?.unit_price ?? 0)}</>
                      ) : (
                        <></>
                      )}
                    </TableCell>

                    <TableCell sx={{ textAlign: 'right' }}>
                      {row?.vat ? `${row?.vat} %` : ''}
                    </TableCell>

                    <TableCell align="right">
                      {type === 'unit' ? (
                        <>{formatNumber(row?.unit_price_gross ?? 0)}</>
                      ) : (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenViewPrices(row);
                          }}
                        >
                          <VisibilityOutlinedIcon color="primary" />
                        </IconButton>
                      )}
                    </TableCell>

                    <TableCell sx={{ textAlign: 'left' }}>
                      {row?.currency?.name}
                    </TableCell>

                    <TableCell sx={{ textAlign: 'left' }}>
                      {row?.service_type?.name ?? ''}
                    </TableCell>

                    <TableCell>
                      <MoreOptions
                        disabledEdit={!(isAdmin || isFinance)}
                        disabledDelete={!isAdmin}
                        clickOnEdit={(e?: any) => {
                          e?.stopPropagation?.();
                          onEdit(row);
                        }}
                        clickOnDelete={(e?: any) => {
                          e?.stopPropagation?.();
                          onDelete(row);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Item>
    </Grid>
  );
}